/**
 * BlastBars — compact horizontal bars summarising the Impact analyzer's
 * blast-radius tier distribution (doc 147 §5.4 redesign).
 *
 * Pure render: each row shows label · bar · count. Bar width is
 * proportional to `count / max(count)` so the eye picks the heaviest tier
 * in one glance. No animation, no state — palette colours are passed in
 * so the parent owns themed colours.
 */
import type { JSX } from 'react';

export interface BlastTier {
  depth: number;
  count: number;
  label: string;
  /** CSS colour (var() or hex) used for both the bar and the label dot. */
  color: string;
}

export interface BlastBarsProps {
  tiers: readonly BlastTier[];
  ariaLabel: string;
}

const ROW_H = 22;
const ROW_GAP = 6;
const LABEL_W = 110;
const COUNT_W = 36;
const BAR_W = 220;

export function BlastBars({ tiers, ariaLabel }: BlastBarsProps): JSX.Element {
  const max = Math.max(1, ...tiers.map((t) => t.count));
  const height = tiers.length * ROW_H + Math.max(0, tiers.length - 1) * ROW_GAP;
  const width = LABEL_W + BAR_W + COUNT_W + 16;
  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      style={{ maxWidth: width, display: 'block' }}
    >
      {tiers.map((t, i) => {
        const y = i * (ROW_H + ROW_GAP);
        const fillW = (t.count / max) * BAR_W;
        return (
          <g key={t.depth}>
            <circle cx="6" cy={y + ROW_H / 2} r="4" fill={t.color} />
            <text
              x="18"
              y={y + ROW_H / 2 + 4}
              fill="var(--text2)"
              fontFamily="var(--ff-mono)"
              fontSize="11"
            >
              {t.label}
            </text>
            <rect
              x={LABEL_W}
              y={y + 4}
              width={BAR_W}
              height={ROW_H - 8}
              rx="3"
              fill="var(--s3)"
              opacity="0.6"
            />
            <rect
              className="tier-fill"
              x={LABEL_W}
              y={y + 4}
              width={fillW}
              height={ROW_H - 8}
              rx="3"
              fill={t.color}
            />
            <text
              x={LABEL_W + BAR_W + 8}
              y={y + ROW_H / 2 + 4}
              fill="var(--text)"
              fontFamily="var(--ff-display)"
              fontWeight="700"
              fontSize="13"
            >
              {t.count}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
